import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { X, Pencil, FileText, Loader2 } from 'lucide-react';
import { useAuthQuery } from '@/hooks/useAuthQuery';
import { useRealtimeQuery } from '@/hooks/useRealtimeQuery';
import { supabase } from '@/integrations/supabase/client';
import type { Project } from '@/types/project';
import { useProjectStakeholders, useProjectTeamLeads, useRemoveStakeholder, useRemoveTeamLead, useAddTeamLead } from '@/hooks/useStakeholderData';
import { useCreateAndAssignStakeholder } from '@/hooks/useStakeholderAssignments';
import { StakeholderEditDialog } from '@/components/admin/StakeholderEditDialog';
import { StakeholderFileUpload } from '@/components/admin/StakeholderFileUpload';
import { getSignedFileUrl, uploadStakeholderFile } from '@/lib/storage';
import type { Stakeholder, StakeholderAttachment } from '@/pages/admin/StakeholderManagement';
import type { FileUploadResult } from '@/lib/storage';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useDialogStatePersisted } from '@/hooks/useDialogStatePersisted';
import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';

type FileWithPreview = {
  file: File;
  preview?: string;
  name: string;
  type: string;
  size: number;
  id: string; // ✅ Stable React key
};

interface StakeholderListDialogProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

export const StakeholderListDialog: React.FC<StakeholderListDialogProps> = ({ project, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const { getStandardValue, setStandardValue } = useLocalStorageManager();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [manualForm, setManualForm] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState<FileWithPreview[]>([]);
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);
  
  // Persist editingStakeholderId to localStorage to maintain dialog state across navigation
  const [editingStakeholderId, setEditingStakeholderId] = useState<string | null>(() => {
    return getStandardValue(`stakeholder-edit-id-${project?.id}`, null, 'APP');
  });
  const editStakeholderDialog = useDialogStatePersisted(`stakeholder-edit-${project?.id}`);
  
  // Persist editingStakeholderId changes to localStorage
  useEffect(() => {
    setStandardValue(`stakeholder-edit-id-${project?.id}`, editingStakeholderId, 'APP');
  }, [editingStakeholderId, setStandardValue, project?.id]);
  
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const stakeholderListRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(true); // ✅ PHASE 3: Track mounted state

  // Fetch data using server state
  const { data: stakeholders = [], isLoading: stakeholdersLoading } = useProjectStakeholders(project?.id);
  const { data: teamLeads = [], isLoading: teamLeadsLoading } = useProjectTeamLeads(project?.id);
  
  // Derive editingStakeholder from stakeholders data based on editingStakeholderId
  const editingStakeholder = useMemo(() => {
    if (!editingStakeholderId || !stakeholders) return null;
    return stakeholders.find(s => s.id === editingStakeholderId) as Stakeholder | null;
  }, [editingStakeholderId, stakeholders]);

  // Mutations
  const createAndAssignStakeholder = useCreateAndAssignStakeholder();
  const addTeamLeadMutation = useAddTeamLead();
  const removeStakeholderMutation = useRemoveStakeholder();
  const removeTeamLeadMutation = useRemoveTeamLead();

  // ✅ PHASE 3: Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Fetch users with ROLE_TEAM_LEAD using RPC
  const { data: teamLeadUsers = [] } = useAuthQuery<any[]>({
    queryKey: ['team-lead-users'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_team_lead_users');
      
      if (error) throw error;
      return data || [];
    },
    enabled: isOpen,
  });

  // ✅ PHASE 2: Batch fetch signed URLs with debounce (N+1 query fix)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const fetchUrlsBatch = async () => {
      const allAttachments = stakeholders.flatMap(s => (s.attachments as StakeholderAttachment[]) || []);
      const pending = allAttachments.filter(f => !signedUrls[f.path]);
      
      if (pending.length === 0) return;

      const results = await Promise.allSettled(
        pending.map(f => getSignedFileUrl(f.path, 3000))
      );

      const newUrls: Record<string, string> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          newUrls[pending[i].path] = r.value;
        } else {
          console.error(`Failed to load signed URL for ${pending[i].path}:`, r.reason);
        }
      });

      setSignedUrls(prev => ({ ...prev, ...newUrls }));
    };
    
    if (stakeholders.length > 0) {
      // Debounce 300ms to avoid fetch during scroll
      timeoutId = setTimeout(fetchUrlsBatch, 300);
    }
    
    return () => clearTimeout(timeoutId);
  }, [stakeholders]);


  const handleUserSelection = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUserIds([...selectedUserIds, userId]);
    } else {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    }
  };

  const handleAddSelectedTeamLeads = async () => {
    if (selectedUserIds.length === 0 || !project) return;

    try {
      // Add each selected user as team lead
      for (const userId of selectedUserIds) {
        await addTeamLeadMutation.mutateAsync({
          projectId: project.id,
          userId
        });
      }
      setSelectedUserIds([]);
    } catch (error) {
      console.error('Error adding team leads:', error);
    }
  };

  const handleRemoveStakeholder = (assignmentId: string) => {
    removeStakeholderMutation.mutate({ id: assignmentId });
  };

  const handleRemoveTeamLead = (teamLeadId: string) => {
    removeTeamLeadMutation.mutate({ id: teamLeadId });
  };


  const handleAddManualStakeholder = async () => {
    if (!manualForm.name.trim() || !project?.id) {
      toast({ title: 'Missing required fields', variant: 'destructive' });
      return;
    }

    let createdStakeholderId: string | null = null;
    let assigned = false;

    setIsSubmittingManual(true);

    try {
      // 1. Create stakeholder with notes
      const { data: stakeholder, error: stErr } = await supabase
        .from('stakeholders')
        .insert({
          name: manualForm.name.trim(),
          email: manualForm.email?.trim() || null,
          phone: manualForm.phone?.trim() || null,
          notes: manualForm.notes?.trim() || null,
        })
        .select()
        .single();
      
      if (stErr || !stakeholder) throw stErr || new Error('Failed to create stakeholder');
      createdStakeholderId = stakeholder.id;

      // 2. Upload files in parallel
      const results = await Promise.allSettled(
        uploadedFiles.map(f => uploadStakeholderFile(stakeholder.id, f.file))
      );

      const attachments: StakeholderAttachment[] = results
        .filter((r): r is PromiseFulfilledResult<FileUploadResult> => r.status === 'fulfilled')
        .map(r => ({
          path: r.value.path,
          name: r.value.name,
          type: r.value.type,
          size: r.value.size,
          uploadedAt: r.value.uploadedAt
        }));

      // Toast errors for failed uploads
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.error(`Upload failed: ${uploadedFiles[i].name}`, r.reason);
          toast({ 
            title: 'Upload failed', 
            description: `Failed to upload: ${uploadedFiles[i].name}`,
            variant: 'destructive' 
          });
        }
      });

      // 3. Update attachments if any files uploaded successfully
      if (attachments.length > 0) {
        const { error: updErr } = await supabase
          .from('stakeholders')
          .update({ attachments: attachments as any })
          .eq('id', stakeholder.id);
        if (updErr) throw updErr;
      }

      // 4. Assign to project (critical step)
      const { error: assignErr } = await supabase
        .from('project_stakeholder_assignments')
        .insert({
          project_id: project.id,
          stakeholder_id: stakeholder.id,
          role: 'Stakeholder'
        });
      if (assignErr) throw assignErr;
      assigned = true;

      toast({ title: 'Stakeholder added successfully' });

      // Reset form
      setManualForm({ name: '', email: '', phone: '', notes: '' });

      // ✅ Cleanup preview URLs before reset
      uploadedFiles.forEach(f => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      setUploadedFiles([]);

      // ✅ Invalidate queries in Near → Far order with await
      await queryClient.invalidateQueries({ queryKey: ['project-stakeholders', project.id] });
      await queryClient.invalidateQueries({ queryKey: ['stakeholders'] });
      await queryClient.invalidateQueries({ queryKey: ['stakeholders', 'optimized', 'v2'] });

      // ✅ Auto-scroll to new item after refetch completes
      stakeholderListRef.current?.scrollTo({
        top: stakeholderListRef.current.scrollHeight,
        behavior: 'smooth'
      });

    } catch (err) {
      console.error('[StakeholderListDialog] Add failed:', err);
      
      // Rollback: Delete orphan stakeholder if not assigned
      if (createdStakeholderId && !assigned) {
        console.warn('[StakeholderListDialog] Rolling back stakeholder creation:', createdStakeholderId);
        await supabase
          .from('stakeholders')
          .delete()
          .eq('id', createdStakeholderId);
      }
      
      toast({ 
        title: 'Failed to add stakeholder', 
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive' 
      });
    } finally {
      // ✅ PHASE 3: Check if mounted before setState
      if (isMounted.current) {
        setIsSubmittingManual(false);
      }
    }
  };

  if (!project) return null;

  const isLoading = stakeholdersLoading || teamLeadsLoading;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quản lý Stakeholder - {project.project_name}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-muted-foreground">Đang tải...</div>
          </div>
        ) : (
          <div className="space-y-6 p-4">
            {/* Stakeholder Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Danh sách Stakeholder</h3>
              
              {/* Current stakeholders */}
              <div 
                ref={stakeholderListRef}
                className="border rounded-lg p-4 max-h-[500px] overflow-y-auto"
              >
                <h4 className="font-medium mb-3">Stakeholder hiện tại</h4>
                {stakeholders.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Chưa có stakeholder nào</p>
                ) : (
                  <div className="space-y-2">
                    {stakeholders.map((stakeholder) => (
                      <div key={stakeholder.assignment_id} className="flex flex-col gap-2 p-3 border rounded">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium">{stakeholder.name}</div>
                            <div className="text-sm text-muted-foreground">
                              Vai trò: {stakeholder.role}
                              {stakeholder.email && ` • ${stakeholder.email}`}
                              {stakeholder.phone && ` • ${stakeholder.phone}`}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                            onClick={() => {
                                setEditingStakeholderId(stakeholder.id);
                                editStakeholderDialog.open();
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveStakeholder(stakeholder.assignment_id!)}
                              disabled={removeStakeholderMutation.isPending}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {stakeholder.notes && (
                          <div className="text-sm text-muted-foreground border-t pt-2">
                            <span className="font-medium">Ghi chú:</span> {stakeholder.notes}
                          </div>
                        )}

                        {stakeholder.attachments && (stakeholder.attachments as StakeholderAttachment[]).length > 0 && (
                          <div className="border-t pt-2">
                            <span className="text-sm font-medium">File đính kèm:</span>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                              {(stakeholder.attachments as StakeholderAttachment[]).map((file, idx) => {
                                const signedUrl = signedUrls[file.path];
                                const isImage = file.type.startsWith('image/');

                                return (
                                  <a
                                    key={idx}
                                    href={signedUrl || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="border rounded overflow-hidden hover:opacity-80 transition"
                                    onClick={(e) => {
                                      if (!signedUrl) {
                                        e.preventDefault();
                                        toast({ title: 'File URL chưa sẵn sàng', variant: 'destructive' });
                                      }
                                    }}
                                  >
                                    {!signedUrl ? (
                                      <div className="w-full h-16 flex items-center justify-center bg-muted">
                                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                      </div>
                                    ) : isImage ? (
                                      <img
                                        src={signedUrl}
                                        alt={file.name}
                                        className="w-full h-16 object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-16 flex items-center justify-center bg-muted">
                                        <FileText className="h-6 w-6 text-muted-foreground" />
                                      </div>
                                    )}
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add stakeholder forms */}
              <div className="space-y-4">
                {/* Manual creation form */}
                <div 
                  className="border rounded-lg p-4"
                  aria-busy={isSubmittingManual}
                  aria-label="Create new stakeholder form"
                >
                  <h4 className="font-medium mb-3">Tạo mới Stakeholder</h4>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label htmlFor="name">Tên *</Label>
                        <Input
                          id="name"
                          value={manualForm.name}
                          onChange={(e) => setManualForm({...manualForm, name: e.target.value})}
                          placeholder="Nhập tên"
                          disabled={isSubmittingManual}
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={manualForm.email}
                          onChange={(e) => setManualForm({...manualForm, email: e.target.value})}
                          placeholder="example@email.com"
                          disabled={isSubmittingManual}
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Số điện thoại</Label>
                        <Input
                          id="phone"
                          value={manualForm.phone}
                          onChange={(e) => setManualForm({...manualForm, phone: e.target.value})}
                          placeholder="0123456789"
                          disabled={isSubmittingManual}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="stakeholder-notes">Ghi chú</Label>
                      <Textarea
                        id="stakeholder-notes"
                        placeholder="Enter notes (optional)"
                        value={manualForm.notes}
                        onChange={(e) => setManualForm(prev => ({ ...prev, notes: e.target.value }))}
                        disabled={isSubmittingManual}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Đính kèm</Label>
                      <StakeholderFileUpload
                        value={uploadedFiles}
                        onFilesChange={setUploadedFiles}
                        disabled={isSubmittingManual}
                      />
                    </div>

                    <div className="flex justify-between items-center pt-2">
                      <div className="text-sm text-muted-foreground">Vai trò: Stakeholder</div>
                      <Button 
                        onClick={handleAddManualStakeholder}
                        disabled={isSubmittingManual || !manualForm.name.trim()}
                        size="sm"
                        aria-disabled={isSubmittingManual}
                      >
                        {isSubmittingManual ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          'Thêm Stakeholder'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Team Lead Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Danh sách Lead</h3>
              
              {/* Current team leads */}
              <div className="border rounded-lg p-4 max-h-60 overflow-y-auto">
                <h4 className="font-medium mb-3">Team Lead hiện tại</h4>
                {teamLeads.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Chưa có team lead nào</p>
                ) : (
                  <div className="space-y-2">
                    {teamLeads.map((teamLead) => (
                      <div key={teamLead.id} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex-1">
                          <div className="font-medium">{teamLead.name}</div>
                          <div className="text-sm text-muted-foreground">{teamLead.email}</div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveTeamLead(teamLead.id)}
                          disabled={removeTeamLeadMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add team leads from system */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Thêm Team Lead từ hệ thống</h4>
                {teamLeadUsers.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Không có team lead nào trong hệ thống</p>
                ) : (
                  <div className="space-y-4">
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {teamLeadUsers.map((user) => {
                        const isExisting = teamLeads.some(tl => tl.user_id === user.id);
                        const isSelected = selectedUserIds.includes(user.id);
                        
                        return (
                          <div key={user.id} className="flex items-center space-x-3 p-2 border rounded">
                            <Checkbox
                              checked={isSelected}
                              disabled={isExisting}
                              onCheckedChange={(checked) => handleUserSelection(user.id, checked as boolean)}
                            />
                            <div className="flex-1">
                              <div className="font-medium">{user.full_name || 'N/A'}</div>
                              <div className="text-sm text-muted-foreground">
                                {user.email}
                                {isExisting && ' (Đã có trong danh sách)'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Button 
                      onClick={handleAddSelectedTeamLeads}
                      disabled={selectedUserIds.length === 0 || addTeamLeadMutation.isPending}
                      size="sm"
                    >
                      Thêm đã chọn ({selectedUserIds.length})
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button variant="outline" onClick={onClose}>
                Đóng
              </Button>
            </div>
          </div>
        )}
      </DialogContent>

      <StakeholderEditDialog
        stakeholder={editingStakeholder}
        isOpen={editStakeholderDialog.isOpen}
        onClose={() => {
          editStakeholderDialog.close();
          setEditingStakeholderId(null);
        }}
        onSuccess={() => {
          editStakeholderDialog.close();
          setEditingStakeholderId(null);
        }}
        contextProjectId={project?.id}
      />
    </Dialog>
  );
};